import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { Location } from '@angular/common';
import { PurchasingService } from '../../services/purchasing.service';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { PrintService } from '../../services/print.service';

/**
 * Outstanding GRNs — daily review screen.
 *
 * Received from a vendor, no vendor invoice matched. Until now the only way off
 * this list was matching an invoice, so it went back to 2025 and got worked once
 * a year. This screen filters it (vendor / age / never-invoiced) and gives the
 * two ways to close a line:
 *
 *   Write off             — the invoice is never coming. Posts a G/L journal that
 *                           reverses the AP accrual the receipt created.
 *   Invoice already in V8 — the bill was keyed straight to G/L, or it's an internal
 *                           store transfer. Closes the line, posts nothing.
 *
 * Both are audited and reversible (Closed log tab) so a late invoice can still be
 * matched. Server side: apir/purchasing/grns.
 */
@Component({
	selector: 'app-purchasing-open-grns',
	templateUrl: './purchasing-open-grns.component.html',
	styleUrls: ['./purchasing-open-grns.component.scss']
})
export class PurchasingOpenGrnsComponent implements OnInit {
	color: any = 'blue';
	@ViewChild('print_table') printtable: ElementRef;

	tab: 'open' | 'closed' = 'open';

	summary: any = null;
	lines: any[] = [];
	closures: any[] = [];

	loadingSummary = false;
	loadingLines = false;
	loadingClosures = false;
	closing = false;

	msg = '';
	msgKind: 'ok' | 'warn' | 'err' = 'ok';

	filters: any = {
		supplierid: '',
		min_age_days: 90,
		from: '',
		to: '',
		never_invoiced: false,
		search: '',
	};

	agePresets = [
		{ label: 'All ages', value: 0 },
		{ label: 'Over 30 days', value: 30 },
		{ label: 'Over 60 days', value: 60 },
		{ label: 'Over 90 days', value: 90 },
		{ label: 'Over 180 days', value: 180 },
	];

	closureFilters: any = { supplierid: '', from: '', to: '', reversed: '' };

	selected: any = {};                 // grnno -> true
	reason = '';

	constructor(
		private purchasingService: PurchasingService,
		private globalSearchService: GlobalSearchService,
		private printService: PrintService,
		private location: Location,
	) {
		this.color = this.globalSearchService.getColor();
	}

	ngOnInit(): void {
		this.loadSummary();
		this.loadLines();
	}

	/* ------------------------------------------------------------- loading */

	loadSummary(): void {
		this.loadingSummary = true;
		this.purchasingService.getOpenGrnSummary().subscribe((r: any) => {
			this.loadingSummary = false;
			this.summary = r && r.success ? r : null;
		}, () => { this.loadingSummary = false; });
	}

	loadLines(): void {
		this.loadingLines = true;
		this.selected = {};
		this.purchasingService.getOpenGrnLines(this.filters).subscribe((r: any) => {
			this.loadingLines = false;
			this.lines = (r && r.lines) ? r.lines : [];
		}, () => { this.loadingLines = false; this.lines = []; });
	}

	loadClosures(): void {
		this.loadingClosures = true;
		this.purchasingService.getGrnClosures(this.closureFilters).subscribe((r: any) => {
			this.loadingClosures = false;
			this.closures = (r && r.closures) ? r.closures : [];
		}, () => { this.loadingClosures = false; this.closures = []; });
	}

	setTab(tab: 'open' | 'closed'): void {
		this.tab = tab;
		if (tab === 'closed' && !this.closures.length) {
			this.loadClosures();
		}
	}

	/* ---------------------------------------------------------- selection */

	toggleAll(checked: boolean): void {
		this.selected = {};
		if (checked) {
			this.lines.forEach((l: any) => { this.selected[l.grnno] = true; });
		}
	}

	get allChecked(): boolean {
		return this.lines.length > 0 && this.lines.every((l: any) => !!this.selected[l.grnno]);
	}

	get selectedGrns(): any[] {
		return Object.keys(this.selected).filter((k) => this.selected[k]);
	}

	get selectedCount(): number {
		return this.selectedGrns.length;
	}

	get selectedValue(): number {
		const picked = this.selectedGrns;
		return this.lines
			.filter((l: any) => picked.indexOf(String(l.grnno)) !== -1)
			.reduce((t: number, l: any) => t + (parseFloat(l.open_value) || 0), 0);
	}

	get openValueTotal(): number {
		return this.lines.reduce((t: number, l: any) => t + (parseFloat(l.open_value) || 0), 0);
	}

	/* -------------------------------------------------------------- close */

	close(): void {
		if (!this.selectedCount || this.closing) {
			return;
		}
		if (!this.reason.trim()) {
			this.setMsg('Type a reason first — it goes on the audit row.', 'warn');
			return;
		}

		const what = 'Close ' + this.selectedCount + ' GRN line(s) for '
			+ this.money(this.selectedValue) + ' off the work list?\n\n'
			+ 'No G/L is posted — the accrual stays exactly where it is. '
			+ 'This only takes the line off Outstanding GRNs and off the Add Vendor Invoice picker.';

		if (!confirm(what + '\n\nReversible from the Closed log.')) {
			return;
		}

		this.closing = true;
		this.purchasingService.closeGrns({
			grns: this.selectedGrns.map((g: any) => parseInt(g, 10)),
			reason: this.reason.trim(),
		}).subscribe((r: any) => {
			this.closing = false;
			if (!r || !r.success) {
				this.setMsg((r && r.error) ? r.error : 'Close failed.', 'err');
				return;
			}
			const skipped = r.skipped ? ' — ' + r.skipped + ' skipped (already invoiced)' : '';
			this.setMsg('Closed ' + r.closed + ' line(s), ' + this.money(r.value)
				+ ' taken off the list. No G/L posted.' + skipped, r.skipped ? 'warn' : 'ok');
			this.reason = '';
			this.loadSummary();
			this.loadLines();
			this.closures = [];
		}, () => {
			this.closing = false;
			this.setMsg('Close failed.', 'err');
		});
	}

	reverseClosure(c: any): void {
		if (!confirm('Reverse this closure? GRN ' + c.grnno + ' goes back on the outstanding list.')) {
			return;
		}
		this.purchasingService.reverseGrnClosure(c.id).subscribe((r: any) => {
			if (!r || !r.success) {
				this.setMsg((r && r.error) ? r.error : 'Reversal failed.', 'err');
				return;
			}
			this.setMsg('GRN ' + r.grnno + ' reopened.', 'ok');
			this.loadClosures();
			this.loadSummary();
			this.loadLines();
		});
	}

	/* ------------------------------------------------------------ helpers */

	pickVendor(supplierid: string): void {
		this.filters.supplierid = (this.filters.supplierid === supplierid) ? '' : supplierid;
		this.loadLines();
	}

	clearFilters(): void {
		this.filters = { supplierid: '', min_age_days: 0, from: '', to: '', never_invoiced: false, search: '' };
		this.loadLines();
	}

	ageClass(l: any): string {
		const d = parseInt(l.age_days, 10);
		if (isNaN(d)) { return 'ogc-age--bad'; }        // no usable receipt date
		if (d > 180) { return 'ogc-age--bad'; }
		if (d > 90) { return 'ogc-age--warn'; }
		return '';
	}

	money(n: any): string {
		return '$' + (parseFloat(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
	}

	private setMsg(m: string, kind: 'ok' | 'warn' | 'err'): void {
		this.msg = m;
		this.msgKind = kind;
	}

	exportXls(): void {
		const encoded: string = this.globalSearchService.base64encode(this.printtable.nativeElement.innerHTML);
		this.printService.xls({ content: encoded, filename: 'Outstanding GRNs' }).subscribe((result: any) => {
			this.globalSearchService.downloadXls(result.content, 'Outstanding GRNs');
		});
	}

	exportPdf(): void {
		const encoded: string = this.globalSearchService.base64encode(this.printtable.nativeElement.innerHTML);
		this.printService.pdf({ content: encoded, filename: 'Outstanding GRNs' }).subscribe((result: any) => {
			this.globalSearchService.downloadPdf(result.content, 'Outstanding GRNs');
		});
	}

	back(): void {
		this.location.back();
	}
}
